import React from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { MapPin, X, Plus, Search, Sparkles } from 'lucide-react';
import { US_STATES } from '../../utils/states';
import { SmartCityAutocomplete } from '../SmartCityAutocomplete';
import CategorySelector from '../CategorySelector';

const SidebarFilterContent = ({
  selectedState, handleStateChange, primaryCity, handlePrimaryCityChange,
  isPrimaryCityOverridden, user, additionalCities, handleRemoveCity,
  showAddCity, setShowAddCity, newCityState, setNewCityState, handleAddCity,
  selectedCategory, setSelectedCategory, publishedFilter, setPublishedFilter,
  showMyHolds, setShowMyHolds, showAllHolds, setShowAllHolds,
  showPrivateOverlay, setShowPrivateOverlay, setFindSimilarOpen,
  eventSearchQuery, setEventSearchQuery, typeSearchQuery, setTypeSearchQuery,
  searchTimeRange, setSearchTimeRange, onSearchEvents, filteredCount,
  isGuestMode, onGuestAction, navigate
}) => (
  <>
    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Location</h3>
      <div className="space-y-2">
        <Select value={selectedState} onValueChange={handleStateChange}>
          <SelectTrigger className="h-9 text-xs" data-testid="calendar-state-filter">
            <SelectValue placeholder="Select a state" />
          </SelectTrigger>
          <SelectContent>
            {US_STATES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SmartCityAutocomplete
          value={primaryCity}
          onChange={handlePrimaryCityChange}
          state={selectedState}
          placeholder="Primary city..."
          isDefault={!isPrimaryCityOverridden && !!user?.city}
          dataTestId="calendar-city-filter"
        />
        {additionalCities.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs bg-slate-50 dark:bg-slate-800 rounded-md px-2 py-1.5">
            <MapPin className="h-3 w-3 text-slate-400 dark:text-slate-500" />
            <span className="flex-1 truncate text-slate-700 dark:text-slate-300">{c.city}, {c.state}</span>
            <button onClick={() => handleRemoveCity(i)} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {additionalCities.length < 7 && !showAddCity && (
          <button onClick={() => { setNewCityState(selectedState); setShowAddCity(true); }} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid="add-city-btn">
            <Plus className="h-3 w-3" /> Add city
          </button>
        )}
        {showAddCity && (
          <div className="space-y-1.5">
            <SmartCityAutocomplete value="" onChange={(city) => handleAddCity(city, newCityState)} state={newCityState} placeholder="Add city..." dataTestId="add-city-input" />
            <button onClick={() => setShowAddCity(false)} className="text-xs text-slate-400">Cancel</button>
          </div>
        )}
      </div>
    </div>

    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Category</h3>
      <CategorySelector value={selectedCategory === 'all' ? '' : selectedCategory} onChange={(id) => setSelectedCategory(id || 'all')} placeholder="All Categories" showExamples={false} allowCustom={false} size="sm" dataTestId="calendar-category-filter" />
    </div>

    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Type <span className="normal-case font-normal">(optional)</span></h3>
      <input
        type="text"
        value={typeSearchQuery}
        onChange={(e) => setTypeSearchQuery(e.target.value)}
        placeholder="e.g. womens, karaoke, lgbtq+..."
        spellCheck="true"
        autoComplete="off"
        className="w-full h-9 px-3 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        data-testid="type-search-input"
      />
    </div>

    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Specific Event (Optional)</h3>
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={eventSearchQuery}
            onChange={(e) => setEventSearchQuery(e.target.value)}
            placeholder='e.g. "Summer Music Fest", "Art Walk"...'
            spellCheck="true"
            autoComplete="off"
            className="w-full h-9 pl-8 pr-3 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            data-testid="event-search-input"
          />
          {eventSearchQuery && (
            <button onClick={() => setEventSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <Select value={searchTimeRange} onValueChange={(v) => {
          if (v === '6months' || v === '1year') {
            navigate('/deep-search');
            return;
          }
          setSearchTimeRange(v);
        }}>
          <SelectTrigger className="h-9 text-xs" data-testid="search-time-range">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1month">1 Month</SelectItem>
            <SelectItem value="3months">3 Months</SelectItem>
            <SelectItem value="6months">6 Months (Deep Search)</SelectItem>
            <SelectItem value="1year">1 Year (Deep Search)</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="w-full text-xs bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-300 dark:text-slate-900 text-white"
          onClick={onSearchEvents}
          data-testid="search-events-btn"
        >
          <Search className="h-3 w-3 mr-1.5" /> Search Events
          {filteredCount !== null && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500 text-white" data-testid="search-results-count">
              {filteredCount}
            </span>
          )}
        </Button>
      </div>
    </div>

    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Layers</h3>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox checked={publishedFilter === 'all'} onCheckedChange={(v) => setPublishedFilter(v ? 'all' : 'mine')} data-testid="layer-all-published" />
          <span className="w-2 h-2 rounded-full bg-emerald-500" /> All Published Events
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox checked={publishedFilter === 'mine'} onCheckedChange={(v) => setPublishedFilter(v ? 'mine' : 'all')} data-testid="layer-mine-only" />
          <span className="w-2 h-2 rounded-full bg-emerald-600" /> My Published Events
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox checked={showMyHolds} onCheckedChange={setShowMyHolds} data-testid="layer-my-holds" />
          <span className="w-2 h-2 rounded-full bg-blue-500" /> My Hold(s)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox checked={showAllHolds} onCheckedChange={setShowAllHolds} data-testid="layer-all-holds" />
          <span className="w-2 h-2 rounded-full bg-amber-500" /> All Hold(s)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox checked={showPrivateOverlay} onCheckedChange={setShowPrivateOverlay} data-testid="layer-private" />
          <span className="w-2 h-2 rounded-full bg-violet-500" /> Private Events
        </label>
      </div>
    </div>

    <div className="mb-5">
    <div className="mb-5">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-500 dark:text-emerald-400 mb-2">Deep Search</h3>
      <div className="p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2">
        <p className="text-[10px] text-slate-500 dark:text-slate-400">AI-powered event intelligence:</p>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Find similar events
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Competition analysis
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Overlapping events
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Extended search (6 months)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Extended search (1 year)
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
          <Checkbox defaultChecked disabled className="data-[state=checked]:bg-emerald-600" />
          Monitoring & alerts
        </label>
        <Button 
          size="sm" 
          className="w-full text-xs mt-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => isGuestMode ? onGuestAction?.() : navigate('/deep-search')}
          data-testid="calendar-deep-search-btn"
        >
          <Sparkles className="h-3 w-3 mr-1" /> Open Deep Search
        </Button>
      </div>
    </div>
    </div>

    <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 mb-2">Legend</h3>
      <div className="grid grid-cols-2 gap-1.5 mb-4">
        {[{ color: 'bg-emerald-500', label: 'Published' }, { color: 'bg-blue-500', label: 'My Holds' }, { color: 'bg-amber-500', label: 'Other Holds' }, { color: 'bg-violet-500', label: 'Private' }].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
            <span className={`w-2 h-2 rounded-full ${color}`} /> {label}
          </div>
        ))}
      </div>
      <Button
        size="sm"
        className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
        onClick={() => {
          if (isGuestMode) { onGuestAction?.(); return; }
          navigate('/deep-search');
        }}
        data-testid="deep-search-btn"
      >
        <Sparkles className="h-3 w-3 mr-1" /> Deep Search
      </Button>
    </div>
  </>
);

export default SidebarFilterContent;
